import { DoseChangeHolds } from "./DoseChangeHolds";
import { type ReactNode, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import {
  ArrowLeft,
  CalendarClock,
  FileText,
  Image as ImageIcon,
  MessageSquare,
  Paperclip,
  Search,
  Send,
  UserRound,
} from 'lucide-react';

import {
  MessageDocumentCard,
  RichMessageContent,
} from '@/components/content/RichMessageContent';
import { Badge, Button, Card } from '@/design-system/primitives';
import {
  useClinicianPatientSearch,
  useClinicianSession,
  usePatientAttachments,
  useSendPatientMessage,
  useWorklistPatientBrief,
} from '@/lib/api/clinicianHooks';
import type {
  ConversationAttachment,
  ConversationMessage,
} from '@/types/clinician';
import { PatientChartDrawer, type ChartView } from './ConversationScreen';
import { clinicianMessageError } from './messageError';
import { SubscriptionStatusSummary } from './SubscriptionStatus';
import { OrderPharmacyLabel } from './OrderPharmacyLabel';
import { PatientPrescriber } from './PrescriberAssignment';

export function PatientDirectoryScreen() {
  const { patientId } = useParams();
  return patientId ? (
    <PatientWorkspace patientId={patientId} />
  ) : (
    <PatientSearch />
  );
}

function PatientSearch() {
  const [search, setSearch] = useState('');
  const [activity, setActivity] = useState<'active' | 'inactive' | 'all'>(
    'active',
  );
  const result = useClinicianPatientSearch(search, activity);
  const ready = search.trim().length >= 2;
  return (
    <div className="max-w-[90rem] mx-auto px-20 md:px-40 py-30 md:py-40 flex flex-col gap-20">
      <header>
        <span className="type-label-sm text-ink-muted">PATIENT DIRECTORY</span>
        <h1 className="type-heading-xl mt-4">Find any patient</h1>
        <p className="type-body-sm text-ink-muted mt-5">
          Search across your accessible patient panel—not only patients with an
          open message or prescription task.
        </p>
      </header>
      <label className="relative block">
        <Search
          size={16}
          className="absolute left-12 top-1/2 -translate-y-1/2 text-ink-muted"
          aria-hidden
        />
        <input
          autoFocus
          type="search"
          value={search}
          onChange={(event) => setSearch(event.target.value)}
          placeholder="Search name or email"
          aria-label="Search patients"
          className="w-full min-h-48 pl-40 pr-12 border border-line bg-raised type-body-md outline-none focus:shadow-focus"
        />
      </label>
      <div
        className="flex flex-wrap gap-6"
        role="group"
        aria-label="Patient activity"
      >
        {(
          [
            ['active', 'Active'],
            ['inactive', 'Inactive'],
            ['all', 'All'],
          ] as const
        ).map(([value, label]) => (
          <button
            key={value}
            type="button"
            aria-pressed={activity === value}
            onClick={() => setActivity(value)}
            className={`min-h-34 px-12 rounded-pill border type-label-sm ${
              activity === value
                ? 'bg-ink text-page border-ink'
                : 'border-line text-ink-muted hover:bg-sunken'
            }`}
          >
            {label}
          </button>
        ))}
      </div>
      {!ready && (
        <Card className="p-30 text-center type-body-sm text-ink-muted">
          Enter at least two characters to search.
        </Card>
      )}
      {ready && result.isLoading && (
        <Card className="p-30 text-center type-body-sm text-ink-muted">
          Searching patients…
        </Card>
      )}
      {result.isError && (
        <Card
          className="p-30 text-center type-body-sm text-danger"
          role="alert"
        >
          Patient search could not be loaded. Try again.
        </Card>
      )}
      {ready && result.data?.items.length === 0 && (
        <Card className="p-30 text-center type-body-sm text-ink-muted">
          No accessible patient matches that search.
        </Card>
      )}
      {(result.data?.items.length ?? 0) > 0 && (
        <div className="border border-line-subtle bg-raised divide-y divide-line-subtle">
          {result.data?.items.map((patient) => (
            <Link
              key={patient.id}
              to={`/clinician-portal/patients/${patient.id}`}
              className="p-14 flex items-center gap-12 hover:bg-sunken focus-visible:outline-none focus-visible:shadow-focus"
            >
              <UserRound size={18} className="text-ink-muted shrink-0" />
              <div className="min-w-0 flex-1">
                <p className="type-body-md font-medium truncate">
                  {patient.display_name || patient.email || 'Unknown patient'}
                </p>
                <p className="type-body-sm text-ink-muted truncate">
                  {patient.email}
                </p>
              </div>
              {patient.primary_prescriber && (
                <Badge tone="neutral" size="sm">
                  {patient.primary_prescriber.display_name ||
                    patient.primary_prescriber.email}
                </Badge>
              )}
              <Badge
                tone={
                  patient.subscription_state === 'ended' ? 'neutral' : 'success'
                }
                size="sm"
              >
                {patient.subscription_state === 'ended'
                  ? 'Inactive'
                  : patient.subscription_state === 'active'
                    ? 'Active plan'
                    : 'Current · no subscription'}
              </Badge>
            </Link>
          ))}
        </div>
      )}
    </div>
  );
}

function PatientWorkspace({ patientId }: { patientId: string }) {
  const brief = useWorklistPatientBrief(patientId);
  const attachments = usePatientAttachments(patientId);
  const { data: session } = useClinicianSession();
  const send = useSendPatientMessage(patientId);
  const [message, setMessage] = useState('');
  const [sent, setSent] = useState(false);
  const [sendError, setSendError] = useState<string | null>(null);
  const [chartOpen, setChartOpen] = useState(false);
  const [chartView, setChartView] = useState<ChartView>(
    'overview',
  );
  if (brief.isLoading) {
    return (
      <div className="p-40 text-center type-body-sm text-ink-muted">
        Loading patient chart…
      </div>
    );
  }
  if (brief.isError || !brief.data) {
    return (
      <div className="p-40 text-center type-body-sm text-danger">
        The patient chart could not be loaded.
      </div>
    );
  }
  const data = brief.data;
  const name = data.profile.display_name || data.profile.email || 'Patient';
  const credentialMissing = Boolean(
    session && !session.capabilities.message_patients,
  );
  const submit = async () => {
    const content = message.trim();
    if (!content || credentialMissing) return;
    setSent(false);
    setSendError(null);
    try {
      await send.mutateAsync({
        content,
        conversation_id: data.recent_conversation_id,
      });
      setMessage('');
      setSent(true);
    } catch (error) {
      setSendError(clinicianMessageError(error));
    }
  };
  return (
    <div className="max-w-[110rem] mx-auto px-15 md:px-30 py-20 md:py-30 flex flex-col gap-16">
      <header className="border-b border-line-subtle pb-14 flex items-start gap-10">
        <Link
          to="/clinician-portal/patients"
          className="w-40 h-40 border border-line inline-flex items-center justify-center hover:bg-sunken"
          aria-label="Back to patient search"
        >
          <ArrowLeft size={16} />
        </Link>
        <div className="min-w-0 flex-1">
          <span className="type-label-xs text-ink-muted">
            PATIENT WORKSPACE
          </span>
          <h1 className="type-heading-xl truncate">{name}</h1>
          <SubscriptionStatusSummary subscription={data.subscription} />
          <p className="type-body-sm text-ink-muted break-all">
            {data.profile.email}
          </p>
          <PatientPrescriber patientId={patientId} />
        </div>
        <Button
          type="button"
          variant="secondary"
          size="sm"
          onClick={() => {
            setChartView('overview');
            setChartOpen(true);
          }}
        >
          <FileText size={13} /> Quick view chart
        </Button>
      </header>

      <DoseChangeHolds patientId={patientId} />
      <div className="grid xl:grid-cols-[minmax(0,1.25fr)_minmax(24rem,0.75fr)] gap-14 items-start">
        <div className="flex flex-col gap-14 min-w-0">
          <section className="border border-line-subtle bg-raised">
            <SectionTitle
              icon={<MessageSquare size={15} />}
              title="Recent Healthie chat"
            />
            <div className="p-12 flex flex-col gap-9 max-h-[34rem] overflow-y-auto">
              {data.recent_messages.length ? (
                data.recent_messages.map((entry) => (
                  <Message key={entry.id} message={entry} />
                ))
              ) : (
                <p className="type-body-sm text-ink-muted">
                  No recent messages.
                </p>
              )}
            </div>
            <div className="p-12 border-t border-line-subtle flex flex-col gap-8">
              <textarea
                value={message}
                onChange={(event) => setMessage(event.target.value)}
                disabled={credentialMissing}
                rows={3}
                placeholder={
                  credentialMissing
                    ? 'Your Healthie authoring credential still needs to be connected'
                    : 'Send a proactive follow-up…'
                }
                className="w-full border border-line bg-sunken px-10 py-8 type-body-sm resize-y disabled:opacity-60"
              />
              <div className="flex items-center justify-between gap-8">
                <span
                  className={`type-body-sm ${sendError ? 'text-danger' : 'text-success'}`}
                  role={sendError ? 'alert' : undefined}
                >
                  {sendError || (sent ? 'Message sent.' : '')}
                </span>
                <Button
                  type="button"
                  size="sm"
                  onClick={submit}
                  loading={send.isPending}
                  disabled={!message.trim() || credentialMissing}
                >
                  <Send size={13} /> Send message
                </Button>
              </div>
            </div>
          </section>

          <section className="border border-line-subtle bg-raised">
            <div className="px-12 py-9 bg-sunken border-b border-line-subtle flex items-center justify-between gap-8">
              <span className="flex items-center gap-7 type-label-sm">
                <FileText size={15} /> Clinical intake highlights
              </span>
              <button
                type="button"
                onClick={() => {
                  setChartView('intakes');
                  setChartOpen(true);
                }}
                className="type-label-xs text-accent hover:underline"
              >
                View all intakes ({data.intake_forms.length})
              </button>
            </div>
            {data.intake_evidence.length ? (
              <div className="grid md:grid-cols-2 gap-px bg-line-subtle">
                {data.intake_evidence.map((item, index) => (
                  <div
                    // Repeat product intakes can contain the same prompt.
                    // eslint-disable-next-line react/no-array-index-key
                    key={`${item.form_name}-${item.question}-${index}`}
                    className="p-11 bg-raised flex flex-col gap-4"
                  >
                    <div className="flex items-start justify-between gap-7">
                      <p className="type-label-xs text-ink-muted">
                        {item.question}
                      </p>
                      <Badge
                        tone={
                          item.importance === 'safety' ? 'danger' : 'warning'
                        }
                        size="sm"
                      >
                        {item.importance}
                      </Badge>
                    </div>
                    <p className="type-body-sm font-medium whitespace-pre-wrap">
                      {item.answer}
                    </p>
                    <p className="text-11 text-ink-muted">
                      {item.form_name}
                      {item.submitted_at
                        ? ` · ${new Date(item.submitted_at).toLocaleDateString()}`
                        : ''}
                    </p>
                  </div>
                ))}
              </div>
            ) : (
              <p className="p-12 type-body-sm text-ink-muted">
                No tagged clinical highlights. Review the full intake history.
              </p>
            )}
          </section>

          <section className="border border-line-subtle bg-raised">
            <SectionTitle
              icon={<FileText size={15} />}
              title="Medications & orders"
            />
            <div className="divide-y divide-line-subtle">
              {data.medications.map((medication) => (
                <div key={medication.id} className="p-11">
                  <p className="type-body-sm font-medium">{medication.name}</p>
                  <p className="type-body-sm text-ink-muted">
                    {[medication.dose, medication.frequency, medication.route]
                      .filter(Boolean)
                      .join(' · ')}
                  </p>
                </div>
              ))}
              {!data.medications.length && (
                <p className="p-12 type-body-sm text-ink-muted">
                  No active medication records.
                </p>
              )}
              {data.orders.map((order, index) => (
                <div
                  // Some historical orders do not expose a stable id.
                  // eslint-disable-next-line react/no-array-index-key
                  key={`${order.product}-${order.date}-${index}`}
                  className="p-11"
                >
                  <p className="type-body-sm font-medium">{order.product}</p>
                  {order.medication && (
                    <p className="type-body-sm text-ink-muted">
                      {order.medication}
                    </p>
                  )}
                  {order.directions && (
                    <p className="type-body-sm mt-3">{order.directions}</p>
                  )}
                  <p className="text-11 text-ink-muted mt-3">
                    {[order.status, order.date].filter(Boolean).join(' · ')}
                    {' · '}
                    <OrderPharmacyLabel order={order} />
                  </p>
                </div>
              ))}
            </div>
          </section>
        </div>

        <aside className="flex flex-col gap-14">
          <section className="border border-line-subtle bg-raised">
            <SectionTitle
              icon={<UserRound size={15} />}
              title="Patient details"
            />
            <dl className="p-12 grid grid-cols-[8rem_1fr] gap-x-8 gap-y-7 type-body-sm">
              <dt className="text-ink-muted">Phone</dt>
              <dd>{data.profile.phone || '—'}</dd>
              <dt className="text-ink-muted">DOB</dt>
              <dd>{data.profile.date_of_birth || '—'}</dd>
              <dt className="text-ink-muted">State</dt>
              <dd>{data.profile.state || '—'}</dd>
              <dt className="text-ink-muted">Sex</dt>
              <dd>{data.profile.sex || '—'}</dd>
            </dl>
          </section>
          <section className="border border-line-subtle bg-raised">
            <SectionTitle
              icon={<CalendarClock size={15} />}
              title="Upcoming appointments"
            />
            <div className="p-12 flex flex-col gap-8">
              {data.appointments.map((appointment) => (
                <div key={appointment.id} className="type-body-sm">
                  <p className="font-medium">
                    {new Date(appointment.starts_at).toLocaleString()}
                  </p>
                  <p className="text-ink-muted">
                    {appointment.appointment_type || appointment.status}
                  </p>
                </div>
              ))}
              {!data.appointments.length && (
                <p className="type-body-sm text-ink-muted">None scheduled.</p>
              )}
            </div>
          </section>
          <section className="border border-line-subtle bg-raised">
            <SectionTitle
              icon={<FileText size={15} />}
              title="Patient documents"
            />
            <div className="p-12 flex flex-col gap-7">
              {data.documents.map((document) => (
                <a
                  key={document.id}
                  href={document.download_url || undefined}
                  target="_blank"
                  rel="noreferrer"
                  className="type-body-sm underline break-words"
                >
                  {document.filename}
                </a>
              ))}
              {!data.documents.length && (
                <p className="type-body-sm text-ink-muted">
                  No documents on file.
                </p>
              )}
            </div>
          </section>
          <section className="border border-line-subtle bg-raised">
            <SectionTitle
              icon={<Paperclip size={15} />}
              title="Patient chat attachments"
            />
            <div className="p-12 flex flex-col gap-8">
              {attachments.isLoading && (
                <p className="type-body-sm text-ink-muted">
                  Loading attachments…
                </p>
              )}
              {attachments.isError && (
                <p className="type-body-sm text-danger">
                  Chat attachments could not be loaded.
                </p>
              )}
              {(attachments.data ?? []).map((attachment) => (
                <a
                  key={attachment.id}
                  href={attachment.url || undefined}
                  target="_blank"
                  rel="noreferrer"
                  className="border border-line-subtle p-8 flex items-center gap-8 hover:bg-sunken"
                >
                  {attachment.kind === 'image' && attachment.url ? (
                    <img
                      src={attachment.url}
                      alt=""
                      className="w-44 h-44 object-cover border border-line-subtle shrink-0"
                    />
                  ) : (
                    <FileText size={18} className="text-ink-muted shrink-0" />
                  )}
                  <span className="min-w-0 flex-1">
                    <span className="block type-body-sm font-medium truncate">
                      {attachment.filename || 'Chat attachment'}
                    </span>
                    <span className="block text-11 text-ink-muted">
                      {attachment.direction === 'patient'
                        ? 'From patient'
                        : 'Sent by care team'}{' '}
                      · {new Date(attachment.message_at).toLocaleDateString()}
                    </span>
                  </span>
                </a>
              ))}
              {!attachments.isLoading &&
                !attachments.isError &&
                (attachments.data?.length ?? 0) === 0 && (
                  <p className="type-body-sm text-ink-muted">
                    No chat attachments on file.
                  </p>
                )}
              <p className="text-11 text-ink-muted">
                Includes files received across this patient’s Healthie chats.
              </p>
            </div>
          </section>
        </aside>
      </div>
      <PatientChartDrawer
        open={chartOpen}
        onClose={() => setChartOpen(false)}
        data={data}
        view={chartView}
        onViewChange={setChartView}
      />
    </div>
  );
}

function SectionTitle({ icon, title }: { icon: ReactNode; title: string }) {
  return (
    <div className="px-12 py-9 bg-sunken border-b border-line-subtle flex items-center gap-7 type-label-sm">
      {icon} {title}
    </div>
  );
}

function Message({ message }: { message: ConversationMessage }) {
  return (
    <div
      className={`max-w-[88%] ${message.author_kind === 'patient' ? '' : 'ml-auto'}`}
    >
      <p className="text-11 text-ink-muted mb-2">
        {message.author_kind === 'patient'
          ? 'Patient'
          : message.author_name || 'Care team'}
      </p>
      <div className="bg-sunken border border-line-subtle p-9 type-body-sm min-w-0 overflow-hidden">
        <RichMessageContent text={message.text} />
        <Attachments attachments={message.attachments} />
      </div>
    </div>
  );
}

function Attachments({
  attachments,
}: {
  attachments: ConversationAttachment[];
}) {
  if (!attachments.length) return null;
  return (
    <div className="mt-7 flex flex-col gap-6">
      {attachments.map((attachment) =>
        attachment.kind === 'image' ? (
          <a
            key={attachment.url}
            href={attachment.url}
            target="_blank"
            rel="noreferrer"
          >
            <img
              src={attachment.url}
              alt={attachment.name || 'Patient attachment'}
              className="max-h-72 max-w-full object-contain border border-line bg-raised"
            />
          </a>
        ) : attachment.kind === 'document' ? (
          <MessageDocumentCard
            key={attachment.url}
            href={attachment.url}
            title={attachment.name || 'View document'}
            mime={attachment.mime}
          />
        ) : (
          <a
            key={attachment.url}
            href={attachment.url}
            target="_blank"
            rel="noreferrer"
            className="inline-flex items-center gap-6 underline"
          >
            <ImageIcon size={13} />
            {attachment.name || 'Open attachment'}
          </a>
        ),
      )}
    </div>
  );
}
